import React, { useEffect } from 'react';
import { X, MapPin, Clock, Car, Shield, ExternalLink, Navigation, Sparkles, AlertCircle, MessageCircle, Phone } from 'lucide-react';
import { Attraction } from '../types';
import { EASY_WAY_CONTACT } from '../data/contactInfo';
import { useLanguage } from '../context/LanguageContext';
import { getLocalizedAttraction } from '../utils/localization';

interface AttractionModalProps {
  attraction: Attraction | null;
  onClose: () => void;
}

export const AttractionModal: React.FC<AttractionModalProps> = ({ attraction: rawAttraction, onClose }) => {
  const { lang, easyWayFleetUrl, easyWayCancunAirportUrl, easyWayPlayaDelCarmenUrl, easyWayWhatsAppUrl, t } = useLanguage();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (rawAttraction) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [rawAttraction, onClose]);

  if (!rawAttraction) return null;

  const attraction = getLocalizedAttraction(rawAttraction, lang);
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rawAttraction.mapQuery || attraction.title)}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 md:p-10 overflow-y-auto bg-[#172A32]/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-[#ede6d8] my-auto text-[#172A32]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label={lang === 'en' ? 'Close modal' : lang === 'pt' ? 'Cerrar modal' : 'Cerrar modal'}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-[#172A32]/70 hover:bg-[#172A32] text-white flex items-center justify-center transition-colors shadow-lg cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Hero Image */}
        <div className="relative h-64 sm:h-72 w-full overflow-hidden bg-[#F7F3EA]">
          <img
            src={attraction.image}
            alt={attraction.title}
            width={800}
            height={320}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#073B4C] via-[#073B4C]/40 to-transparent" />

          {/* Badges on image */}
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-[#0F766E] text-white">
                {attraction.tag}
              </span>
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-white/20 backdrop-blur-md">
                ⭐ {attraction.rating} / 5.0
              </span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold font-['Outfit',sans-serif] leading-tight">
              {attraction.title}
            </h3>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6 sm:p-8 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Quick Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs bg-[#F7F3EA] p-3.5 rounded-xl border border-[#ede6d8]">
            <div className="flex items-center gap-2">
              <Navigation className="w-4 h-4 text-[#0F766E] shrink-0" />
              <div>
                <p className="text-[#4A5A5F] font-medium">{lang === 'en' ? 'Distance from Cancun' : lang === 'pt' ? 'Distância de Cancun' : 'Distancia desde Cancún'}</p>
                <p className="font-bold text-[#172A32]">{attraction.distanceFromCancun}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#0F766E] shrink-0" />
              <div>
                <p className="text-[#4A5A5F] font-medium">{lang === 'en' ? 'Best Time to Visit' : lang === 'pt' ? 'Mejor Horario' : 'Mejor Horario'}</p>
                <p className="font-bold text-[#172A32]">{attraction.bestTime}</p>
              </div>
            </div>
            <div className="col-span-2 sm:col-span-1 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#D6A84B] shrink-0" />
              <div>
                <p className="text-[#4A5A5F] font-medium">{lang === 'en' ? 'Price Range' : lang === 'pt' ? 'Faixa de Preço' : 'Rango de Precio'}</p>
                <p className="font-bold text-[#172A32]">{attraction.priceRange}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-[#4A5A5F] mb-2">
              {lang === 'en' ? 'About this place' : lang === 'pt' ? 'Sobre este lugar' : 'Acerca de este lugar'}
            </h4>
            <p className="text-sm text-[#172A32] leading-relaxed">
              {attraction.fullDescription}
            </p>
          </div>

          {/* Why by Car Highlight Box */}
          <div className="bg-[#0F766E]/5 rounded-xl p-4 border border-[#0F766E]/20 space-y-2">
            <div className="flex items-center gap-2 text-[#073B4C] font-bold text-sm">
              <Car className="w-4 h-4 text-[#0F766E]" />
                <span>{lang === 'en' ? 'Why is driving essential?' : lang === 'pt' ? 'Por que é indispensável ir de carro?' : '¿Por qué es indispensable ir en auto?'}</span>
            </div>
            <p className="text-xs sm:text-sm text-[#172A32] leading-normal">
              {attraction.whyByCar}
            </p>
          </div>

          {/* Parking Info */}
          <div className="bg-[#F7F3EA] rounded-xl p-4 border border-[#ede6d8] space-y-1.5">
            <p className="text-xs font-bold uppercase tracking-wider text-[#4A5A5F]">
              {lang === 'en' ? '🅿️ Parking & Vehicle Access:' : lang === 'pt' ? '🅿️ Estacionamento e Acesso de Veículos:' : '🅿️ Estacionamiento & Acceso Vehicular:'}
            </p>
            <p className="text-xs sm:text-sm text-[#172A32]">
              {attraction.parkingInfo}
            </p>
          </div>

          {/* Easy Way Rent a Car Callout */}
          <div className="bg-gradient-to-r from-[#172A32] to-[#073B4C] text-white rounded-xl p-4 space-y-3">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <p className="text-xs text-[#0F766E] font-bold uppercase tracking-wider">
                  {lang === 'en' ? 'Your best choice for car rental • Easy Way Rent a Car' : lang === 'pt' ? 'Sua melhor opção em aluguel de carros • Easy Way Rent a Car' : 'Su mejor opción en renta de autos • Easy Way Rent a Car'}
                </p>
                <p className="text-sm font-semibold text-[#F7F3EA]">
                  {lang === 'en' ? `Reach ${attraction.title.split('(')[0]} on your own schedule with complete driving freedom` : lang === 'pt' ? `Llega a ${attraction.title.split('(')[0]} a tu propio ritmo y con total independencia al volante` : `Llega a ${attraction.title.split('(')[0]} a tu propio ritmo y con total independencia al volante`}
                </p>
              </div>
              <a
                href={
                  attraction.id === 'playa_del_carmen' || attraction.distanceFromCancun.toLowerCase().includes('playa del carmen')
                    ? easyWayPlayaDelCarmenUrl
                    : easyWayFleetUrl
                }
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-[#0F766E] hover:bg-[#0d695d] text-white font-bold text-xs rounded-lg transition-colors whitespace-nowrap inline-flex items-center gap-1.5 shrink-0"
              >
                  <span>{lang === 'en' ? 'Quote Online' : lang === 'pt' ? 'Cotar Online' : 'Cotizar en Línea'}</span>
                <ExternalLink className="w-3.5 h-3.5 text-white" />
              </a>
            </div>

            <div className="pt-2 border-t border-[#2a4754] flex flex-wrap items-center justify-between gap-2 text-[11px] text-[#F7F3EA]/80">
                  <span className="text-white/80">{lang === 'en' ? 'Questions or immediate quote?' : lang === 'pt' ? 'Perguntas ou cotação imediata?' : '¿Preguntas o cotización inmediata?'}</span>
              <div className="flex items-center gap-2">
                <a
                  href={easyWayWhatsAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-bold text-[#0F766E] hover:text-white bg-[#073B4C]/80 px-2.5 py-1 rounded-md border border-[#0F766E]/40"
                >
                  <MessageCircle className="w-3.5 h-3.5 fill-current text-[#0F766E]" />
                  <span>WhatsApp 24/7</span>
                </a>
                <a
                  href={EASY_WAY_CONTACT.phoneMexico.link}
                  className="inline-flex items-center gap-1 hover:text-white bg-[#2a4754]/40 px-2.5 py-1 rounded-md border border-[#2a4754]"
                >
                  <Phone className="w-3 h-3 text-[#D6A84B]" />
                  <span>998 886 2464</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="bg-[#F7F3EA] px-6 py-4 border-t border-[#ede6d8] flex flex-col sm:flex-row items-center justify-between gap-3">
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-[#172A32] hover:text-[#073B4C] bg-white border border-[#ede6d8] hover:bg-[#F7F3EA] transition-colors shadow-sm"
          >
            <MapPin className="w-4 h-4 text-[#A8471A]" />
            <span>{lang === 'en' ? 'Open in Google Maps' : lang === 'pt' ? 'Abrir no Google Maps' : 'Abrir en Google Maps'}</span>
            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
          </a>

          <button
            onClick={onClose}
            className="w-full sm:w-auto px-5 py-2 text-xs font-semibold text-[#4A5A5F] hover:text-[#172A32] cursor-pointer"
          >
            {lang === 'en' ? 'Close Overview' : lang === 'pt' ? 'Fechar' : 'Cerrar Ficha'}
          </button>
        </div>
      </div>
    </div>
  );
};
